import { getCoreUtils, StringOnlyOf } from "../../../Admin/kReusableCoreTS/kCoreHandler";
import { AppActivity, AppActivityRules } from "./AppActivity";
import { AppMediaRules } from "./AppMediaRules";
import { kJsonObject } from "../../../Admin/kReusableCoreTS/kJsonObject";
import { AppUpdateEvery } from "./AppUpdateEvery";
import { AppMoney as AppMoneyContract, AppMoneyRow } from "./AppMoney";

// The shape of `index.json`, on the reading side.
//
// ⚠ THIS FILE IS THE CONTRACT WITH `Bridge\src\DossierIndexJson.ts` AND NOTHING ENFORCES IT.
// The two live in different targets - the bridge is Node, this is the browser - so a field renamed
// on one side compiles perfectly on the other and produces a page of blanks at runtime. The schema
// NUMBER is the guard: the app refuses a version it does not know rather than rendering nothing and
// looking merely empty. Bump both together or not at all.

/** The only version this build understands. See `DossierIndexJson.Schema`. */
export const APP_INDEX_SCHEMA: number = 1;

export class AppMoney {
    public Amount: number = 0;
    public Currency: string = "";
    public Direction: string = "none";
    public Settled: boolean = false;
    public Due: string = "";
}

/** A task or a money row, already carrying the contact it belongs to. */
export class AppRow {
    public Label: string = "";
    public Value: string = "";
    public Group: string = "";
    public Kind: string = "note";
    public Type: string = "fact";
    public Status: string = "";
    public Date: string = "";
    public Anchor: string = "";
    public Link: string = "";
    public Contact: string = "";
    public Href: string = "";
    public PersonKey: string = "";
    public Money: AppMoney | null = null;
    /** ⭐ The whole money row (money ON only, `AppMoney.RowIn`), or null - an older index. */
    public MoneyRow: AppMoneyRow | null = null;

    // ⭐ TASKS AS A FIRST-CLASS FEATURE (2026-09-26). Written by the service only with `WAC_TASKS=1`;
    // an older index carries none of them, and every one is read as "" rather than guessed - "" is
    // what `AppTaskRules.Enabled` tests to keep the old Tasks view byte-identical for that index.
    /** The evidence message's clock time (`time`), beside `Date`. */
    public Time: string = "";
    /** The server's id: `t-` (a harvested task), `u-` (one the owner added), `r-` (a reply owed). */
    public Id: string = "";
    /** "me" | "them" | "both" | "" - who has to act. */
    public Owner: string = "";
    /** "YYYY-MM-DD" or "". */
    public Due: string = "";
    /** open | snoozed | done | dismissed, or "" on an old index. */
    public State: string = "";
    /** The snooze date ("YYYY-MM-DD") or "". */
    public Until: string = "";
    /** "ai" | "you" | "reply" | "reconnect" (`q-`), or "" on an old index. */
    public Source: string = "";
    /** When the owner last decided this row (done, snoozed, dismissed), ISO, or "". */
    public DecidedIso: string = "";
    /** The row's contact's picture href (`AppContact.Avatar`), linked by page href in `Parse`; "" = none. */
    public Avatar: string = "";
}

export class AppContact {
    public Title: string = "";
    public Href: string = "";
    public PersonKey: string = "";

    /**
     * The WhatsApp address of the conversation - `46701234567@s.whatsapp.net`, `...@g.us`, `...@lid`.
     *
     * ⚠ IT IS THE ONLY FIELD A SEND CAN BE ADDRESSED WITH, and `PersonKey` is NOT a substitute. A
     * person key is an identity (`+46...`, or `name:anna karlsson` for someone the address book
     * could not resolve); a chat id is a route. Sending to a mis-derived target does not fail - it
     * delivers to the wrong person, which cannot be undone.
     */
    public ChatId: string = "";
    public IsGroup: boolean = false;
    public Total: number = 0;
    public LastIso: string = "";
    public DaysSinceLast: number = 0;
    public Standing: string = "";
    public OpenTasks: number = 0;
    public Harvested: boolean = false;
    public Relationship: string = "";
    public Summary: string = "";

    /**
     * The contact's WhatsApp profile picture in the folder (`People/X/Avatar.jpg`), or "" - an old
     * index, or a contact whose picture is hidden or not fetched yet. The list then draws initials.
     * ⛔ Only ever `AppMediaRules.AvatarFile` beside the page (`AppMediaRules.Resolve`).
     */
    public Avatar: string = "";

    /**
     * ⭐ Messages in the last 30 / 90 days and the prior six months (`act`, 2026-09-27) - what the
     * Overview ranks and `AppActivityRules.IsQuiet` reads. Null on an older index: no Overview numbers.
     */
    public Act: AppActivity | null = null;

    /**
     * ⭐ LIVE, NOT FROM `index.json` (2026-09-28, `AppLiveList`): their messages that arrived since the page loaded and
     * the owner has not opened - the card's "N new". 0 = none.
     */
    public Unread: number = 0;

    /**
     * ⭐ LIVE: a chat that began after the last rebuild - no page yet (`Href` ""). Its card opens the chat's
     * PROVISIONAL page (`#n/<chat id>`, drawn by the service from the journal) until the rebuild writes the real one.
     */
    public IsNew: boolean = false;

    /**
     * ⭐ FROM THE PHONE (2026-10-14, `AppChatMarks` <- `/chat/settings?view=marks`): when the chat was pinned (0 = not),
     * the phone's unread count (-1 = marked unread, 0 = read), Chat Lock and the blocklist. Never from `index.json`.
     */
    public PinnedAt: number = 0;
    public PhoneUnread: number = 0;
    public Locked: boolean = false;
    public Blocked: boolean = false;
}

export class AppCounts {
    public People: number = 0;
    public Groups: number = 0;
    public Brief: number = 0;

    /**
     * ⚠ THE HONEST NUMBER. Without it an empty Tasks view reads as "nothing to do" when it actually
     * means nobody has read the conversations yet. Every view that can be empty must be able to say
     * WHICH kind of empty it is.
     */
    public Harvested: number = 0;
    public Owed: number = 0;
}

export class AppIndex {
    public Loaded: boolean = false;
    public Problem: string = "";
    public BuiltIso: string = "";
    public Contacts: AppContact[] = [];
    public Tasks: AppRow[] = [];
    public Money: AppRow[] = [];
    public Counts: AppCounts = new AppCounts();

    /** Every contact whose conversation nobody has harvested yet - what the coverage banner counts. */
    public Unread = (): number => {
        if (getCoreUtils().KErrorMode === true) { return 0; }
        try {
            return this.Contacts.length - this.Counts.Harvested;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppIndex.Unread");
            return 0;
        }
    };
}

export class AppIndexReader {
    public static readonly UnreadableWhy: string = "Your chat list could not be read just now. "
        + "WACommander makes a fresh one " + AppUpdateEvery.Within + " - reload the page then.";

    /**
     * The text as a JSON object, or null. DOCUMENTED EXCEPTION: pure parse of a file in hand.
     * ERROR-SUPPRESSED-JUSTIFIED: text that is not a JSON object is the defined answer null - `Parse` shows it.
     */
    public static ObjectIn = (Text: string): Record<string, unknown> | null => {
        return kJsonObject.ObjectOfJson(String(Text));
    };

    /**
     * ⚠ A REFUSAL, NOT A BEST EFFORT. An index written by a newer bridge may have moved a field
     * this build reads; rendering it anyway produces a screen of blank cards that looks like "you
     * have no contacts". Saying so is the only honest option, and it is a VALIDATION state rather
     * than an error - nothing has gone wrong, the two halves are just different ages.
     */
    public static Parse = (Text: string): AppIndex => {
        if (getCoreUtils().KErrorMode === true) { return new AppIndex(); }
        try {
            const Out: AppIndex = new AppIndex();
            const Raw: Record<string, unknown> | null = AppIndexReader.ObjectIn(Text);
            // ⛔ AN UNREADABLE index.json IS OUTSIDE INPUT, NOT A DEFECT (2026-09-28): it comes from the subscriber's
            // own Dropbox (a torn upload, a hand edit, a proxy's HTML page), so it is a sentence (fate 1). Reported,
            // it would LATCH the app's handler and every button would go dead for the visit.
            if (Raw === null) {
                Out.Problem = AppIndexReader.UnreadableWhy;
                return Out;
            }
            if (Raw.schema !== APP_INDEX_SCHEMA) {
                Out.Problem = "Your chat list was made by a different version of WACommander, so this page cannot "
                    + "show it (version " + String(Raw.schema) + ", this page reads " + String(APP_INDEX_SCHEMA)
                    + "). Reload the page to get the newest version.";
                return Out;
            }
            Out.Loaded = true;
            Out.BuiltIso = AppIndexReader.Text(Raw.builtIso);
            Out.Contacts = AppIndexReader.Contacts(Raw.contacts);
            Out.Tasks = AppIndexReader.Rows(Raw.tasks);
            AppIndexReader.LinkFaces(Out.Contacts, Out.Tasks);
            Out.Money = AppIndexReader.Rows(Raw.money);
            // ⭐ THE REVENUE UPGRADE (`WAC_MONEY`): a money row carrying `id` + `state` is read whole (`AppMoney.RowIn`).
            if (Array.isArray(Raw.money) === true) {
                const Rows: unknown[] = Raw.money as unknown[];
                for (let At: number = 0; At < Rows.length && At < Out.Money.length; At = At + 1) {
                    if (AppMoneyContract.HasState(Rows[At]) === true) { Out.Money[At].MoneyRow = AppMoneyContract.RowIn(Rows[At]); }
                }
            }
            Out.Counts = AppIndexReader.Counts(Raw.counts);
            return Out;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppIndexReader.Parse");
            return new AppIndex();
        }
    };

    /** Each task row gets its contact's picture href, matched on the page href. */
    private static LinkFaces = (Contacts: AppContact[], Rows: AppRow[]): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            const ByHref: Record<string, string> = {};
            for (const One of Contacts) { if (One.Avatar.length > 0) { ByHref[One.Href] = One.Avatar; } }
            for (const Row of Rows) {
                const Face: string | undefined = ByHref[Row.Href];
                if (Face !== undefined) { Row.Avatar = Face; }
            }
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppIndexReader.LinkFaces");
        }
    };

    private static Contacts = (Raw: unknown): AppContact[] => {
        if (getCoreUtils().KErrorMode === true) { return []; }
        try {
            const Out: AppContact[] = [];
            if (Array.isArray(Raw) === false) { return Out; }
            for (const One of Raw as Record<string, unknown>[]) {
                const Row: AppContact = new AppContact();
                Row.Title = AppIndexReader.Text(One.title);
                Row.Href = AppIndexReader.Text(One.href);
                Row.PersonKey = AppIndexReader.Text(One.personKey);
                Row.ChatId = AppIndexReader.Text(One.chatId);
                Row.IsGroup = One.isGroup === true;
                Row.Total = AppIndexReader.Num(One.total);
                Row.LastIso = AppIndexReader.Text(One.lastIso);
                Row.DaysSinceLast = AppIndexReader.Num(One.daysSinceLast);
                Row.Standing = AppIndexReader.Text(One.standing);
                Row.OpenTasks = AppIndexReader.Num(One.openTasks);
                Row.Harvested = One.harvested === true;
                Row.Relationship = AppIndexReader.Text(One.relationship);
                Row.Summary = AppIndexReader.Text(One.summary);
                Row.Act = AppActivityRules.In(One[AppActivityRules.Field]);
                if (AppIndexReader.Text(One.avatar) === AppMediaRules.AvatarFile) {
                    Row.Avatar = AppMediaRules.Resolve(Row.Href, AppMediaRules.AvatarFile);
                }
                Out.push(Row);
            }
            return Out;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppIndexReader.Contacts");
            return [];
        }
    };

    private static Rows = (Raw: unknown): AppRow[] => {
        if (getCoreUtils().KErrorMode === true) { return []; }
        try {
            const Out: AppRow[] = [];
            if (Array.isArray(Raw) === false) { return Out; }
            for (const One of Raw as Record<string, unknown>[]) { Out.push(AppIndexReader.Row(One)); }
            return Out;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppIndexReader.Rows");
            return [];
        }
    };

    private static Row = (One: Record<string, unknown>): AppRow => {
        if (getCoreUtils().KErrorMode === true) { return new AppRow(); }
        try {
            const Row: AppRow = new AppRow();
            Row.Label = AppIndexReader.Text(One.label);
            Row.Value = AppIndexReader.Text(One.value);
            Row.Group = AppIndexReader.Text(One.group);
            Row.Kind = AppIndexReader.Text(One.kind);
            Row.Type = AppIndexReader.Text(One.type);
            Row.Status = AppIndexReader.Text(One.status);
            Row.Date = AppIndexReader.Text(One.date);
            Row.Anchor = AppIndexReader.Text(One.anchor);
            Row.Link = AppIndexReader.Text(One.link);
            Row.Contact = AppIndexReader.Text(One.contact);
            Row.Href = AppIndexReader.Text(One.href);
            Row.PersonKey = AppIndexReader.Text(One.personKey);
            Row.Money = AppIndexReader.Money(One.money);
            AppIndexReader.TaskFields(Row, One);
            return Row;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppIndexReader.Row");
            return new AppRow();
        }
    };

    /**
     * The task fields (2026-09-26), each a string or "". ⚠ DEFENSIVE BY DESIGN: a number where a
     * string belongs reads as "", never as its `String()` - "0" as an id would switch the new Tasks
     * view on for an index that never carried one.
     */
    public static TaskFields = (Row: AppRow, One: Record<string, unknown>): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            Row.Time = AppIndexReader.Text(One.time);
            Row.Id = AppIndexReader.Text(One.id);
            Row.Owner = AppIndexReader.Text(One.owner);
            Row.Due = AppIndexReader.Text(One.due);
            Row.State = AppIndexReader.Text(One.state);
            Row.Until = AppIndexReader.Text(One.until);
            Row.Source = AppIndexReader.Text(One.source);
            Row.DecidedIso = AppIndexReader.Text(One.decidedIso);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppIndexReader.TaskFields");
        }
    };

    /** One raw task row (the `/tasks` answer's `task`) as an `AppRow`. */
    public static RowOf = (Raw: unknown): AppRow | null => {
        if (getCoreUtils().KErrorMode === true) { return null; }
        try {
            if (Raw === null || typeof Raw !== "object" || Array.isArray(Raw) === true) { return null; }
            return AppIndexReader.Row(Raw as Record<string, unknown>);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppIndexReader.RowOf");
            return null;
        }
    };

    private static Money = (Raw: unknown): AppMoney | null => {
        if (getCoreUtils().KErrorMode === true) { return null; }
        try {
            if (Raw === null || typeof Raw !== "object") { return null; }
            const One: Record<string, unknown> = Raw as Record<string, unknown>;
            const Out: AppMoney = new AppMoney();
            Out.Amount = AppIndexReader.Num(One.amount);
            Out.Currency = AppIndexReader.Text(One.currency);
            Out.Direction = AppIndexReader.Text(One.direction);
            Out.Settled = One.settled === true;
            Out.Due = AppIndexReader.Text(One.due);
            return Out;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppIndexReader.Money");
            return null;
        }
    };

    private static Counts = (Raw: unknown): AppCounts => {
        if (getCoreUtils().KErrorMode === true) { return new AppCounts(); }
        try {
            const Out: AppCounts = new AppCounts();
            if (Raw === null || typeof Raw !== "object") { return Out; }
            const One: Record<string, unknown> = Raw as Record<string, unknown>;
            Out.People = AppIndexReader.Num(One.people);
            Out.Groups = AppIndexReader.Num(One.groups);
            Out.Brief = AppIndexReader.Num(One.brief);
            Out.Harvested = AppIndexReader.Num(One.harvested);
            Out.Owed = AppIndexReader.Num(One.owed);
            return Out;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppIndexReader.Counts");
            return new AppCounts();
        }
    };

    private static Text = (Raw: unknown): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            return StringOnlyOf(Raw);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppIndexReader.Text");
            return "";
        }
    };

    // REUSE-JUSTIFIED: index.json counters are JSON numbers; NumOf coerces numeric strings, which would accept malformed producer data.
    private static Num = (Raw: unknown): number => {
        if (getCoreUtils().KErrorMode === true) { return 0; }
        try {
            if (typeof Raw !== "number" || isNaN(Raw) === true) { return 0; }
            return Raw;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppIndexReader.Num");
            return 0;
        }
    };
}
